import { Button, Col, Form, Row } from 'react-bootstrap';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { EditBoolean } from 'components/forms/EditBoolean';
import { EditString } from 'components/forms/EditString';
import { EditText } from 'components/forms/EditText';
import { EditTime } from 'components/forms/EditTime';
import { EditTimeZoneReference } from 'edit/reference/EditTimeZoneReference';
import { useAppSelector } from 'redux/store';
import { isFiscalPeriodConfig, maxPeriodDaysForPattern, MAX_FISCAL_PERIOD_DAYS } from 'utils/fiscalPeriods';

import { EditDateBound } from './EditDateBound';
import type { EditableSchedule } from './EditableSchedule';
import { ScheduleRuleAdvancedFields } from './ScheduleRuleAdvancedFields';
import { ScheduleRulePeriodFields, type ScheduleRuleFieldOptions } from './ScheduleRulePeriodFields';
import {
  MONTH_OPTIONS,
  NTH_OF_MONTH_OPTIONS,
  nthOfPeriodOptionsForMaxDays,
  PERIOD_OPTIONS,
  WEEK_OF_MONTH_OPTIONS,
  WEEK_OF_PERIOD_OPTIONS,
} from './scheduleFieldOptions';

interface Props {
  readonly schedule: EditableSchedule;
}

const fieldOptions = (fiscal: boolean, fiscalMaxDays: number): ScheduleRuleFieldOptions =>
  fiscal
    ? { month: PERIOD_OPTIONS, week: WEEK_OF_PERIOD_OPTIONS, nth: nthOfPeriodOptionsForMaxDays(fiscalMaxDays) }
    : { month: MONTH_OPTIONS, week: WEEK_OF_MONTH_OPTIONS, nth: NTH_OF_MONTH_OPTIONS };

/**
 * The fields of a single opening rule. A pure `observer` view: every control reads a
 * field and assigns it back, with no local state and no validation — the server owns
 * the rules and the dialog surfaces its message.
 *
 * The fields narrow *independently*, and a rule opens the window only where all of
 * the ones that are set agree. Leaving them all empty is meaningful: it opens the
 * window all the time.
 *
 * The nth-of-month and cross-window fields sit behind a toggle. They are rarely used
 * and they dominate the form when they are not — but it starts open whenever the rule
 * already uses one, so nothing an operator has to maintain is hidden from them (see
 * `EditableSchedule.showAdvanced`).
 *
 * With `useFiscalPeriods`, month / week-of-month / nth chrome says "period" so the
 * form does not imply calendar January when the wire still stores period index 1.
 */
export const ScheduleRuleFields = observer<Props>(({ schedule }) => {
  const fiscal = schedule.useFiscalPeriods;
  const unit = fiscal ? 'period' : 'month';
  const fiscalMaxDays = useAppSelector((state) => {
    const row = state.config.items.find((c) => c.key === 'fiscal_periods');
    if (!row || !isFiscalPeriodConfig(row.value)) return MAX_FISCAL_PERIOD_DAYS;
    return maxPeriodDaysForPattern(row.value.pattern);
  });
  const opts = useMemo(() => fieldOptions(fiscal, fiscalMaxDays), [fiscal, fiscalMaxDays]);

  return (
    <>
      <Row className="mb-3">
        <Col md={12}>
          <EditString label="Name" value={schedule.name} onChange={(v) => (schedule.name = v)} />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <EditText label="Description" value={schedule.description} onChange={(v) => (schedule.description = v)} />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <EditTimeZoneReference
            label="Timezone"
            value={schedule.timezone}
            onChange={(v) => (schedule.timezone = v)}
            // Empty is the meaning-carrying state here — it is what says the rule
            // follows its window — so the field says so itself rather than leaving
            // the operator to infer it from a blank.
            placeholder={
              schedule.schedulingWindow.timezone === null ? "use the window's timezone" : `use ${schedule.schedulingWindow.timezone} from the window`
            }
          />
          <Form.Text className="text-muted">Leave empty to use the timezone from the window.</Form.Text>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <EditTime label="Opens at" value={schedule.startTime} onChange={(v) => (schedule.startTime = v)} seconds />
        </Col>
        <Col md={6}>
          <EditTime label="Closes at" value={schedule.endTime} onChange={(v) => (schedule.endTime = v)} seconds />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <EditDateBound
            label="From date"
            date={schedule.dateFrom}
            onDateChange={(v) => (schedule.dateFrom = v)}
            hasTime={schedule.dateFromHasTime}
            onHasTimeChange={(v) => (schedule.dateFromHasTime = v)}
            time={schedule.dateFromTime}
            onTimeChange={(v) => (schedule.dateFromTime = v)}
            timeLabel="Set the opening time on the first day"
            timeInfo="Overrides 'Opens at' on the first day only, so a range can start part-way through a day."
            defaultTime="08:00:00"
          />
        </Col>
        <Col md={6}>
          <EditDateBound
            label="Until date"
            date={schedule.dateUntil}
            onDateChange={(v) => (schedule.dateUntil = v)}
            hasTime={schedule.dateUntilHasTime}
            onHasTimeChange={(v) => (schedule.dateUntilHasTime = v)}
            time={schedule.dateUntilTime}
            onTimeChange={(v) => (schedule.dateUntilTime = v)}
            timeLabel="Set the closing time on the last day"
            timeInfo="Overrides 'Closes at' on the last day only, so a range can end part-way through a day."
            defaultTime="16:00:00"
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <EditBoolean
            label="Use fiscal periods"
            value={schedule.useFiscalPeriods}
            onChange={(v) => (schedule.useFiscalPeriods = v)}
            info="When on, From/Until period, week of period, and nth of period are counted in the global fiscal calendar instead of calendar months. Weekdays are unchanged. Requires a saved Fiscal Periods configuration under Platform → Configuration."
          />
        </Col>
      </Row>

      <ScheduleRulePeriodFields schedule={schedule} unit={unit} opts={opts} />

      <Button variant="link" className="px-0" onClick={() => (schedule.showAdvanced = !schedule.showAdvanced)} aria-expanded={schedule.showAdvanced}>
        {schedule.showAdvanced ? 'Hide advanced' : 'Show advanced'}
      </Button>

      {schedule.showAdvanced ? <ScheduleRuleAdvancedFields schedule={schedule} unit={unit} opts={opts} /> : null}
    </>
  );
});
